import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Rating,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Alert,
  Snackbar,
  TextField,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import affiliateService from '../services/affiliateService';

function AffiliateReviews() {
  const [reviews, setReviews] = useState([]);
  const [filteredReviews, setFilteredReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTab, setCurrentTab] = useState('all');
  const [selectedReview, setSelectedReview] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadReviews(currentTab);
  }, [currentTab]);

  const loadReviews = async (filter) => {
    try {
      setLoading(true);
      const isPublished = filter === 'published' ? true : filter === 'unpublished' ? false : null;
      const data = await affiliateService.getReviews(null, null, isPublished);
      setReviews(data.data || []);
      setFilteredReviews(data.data || []);
    } catch (error) {
      showSnackbar('Erreur lors du chargement des avis', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredReviews(reviews);
    } else {
      const term = searchTerm.toLowerCase();
      const filtered = reviews.filter(review => 
        review.affiliate?.user?.first_name?.toLowerCase().includes(term) ||
        review.affiliate?.user?.last_name?.toLowerCase().includes(term) ||
        review.affiliate?.affiliateCode?.toLowerCase().includes(term) ||
        review.product?.name?.toLowerCase().includes(term) ||
        review.comment?.toLowerCase().includes(term)
      );
      setFilteredReviews(filtered);
    }
  }, [searchTerm, reviews]);

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleTogglePublish = async (id) => {
    try {
      await affiliateService.toggleReviewPublish(id);
      showSnackbar('Statut de publication mis à jour');
      loadReviews(currentTab);
    } catch (error) {
      showSnackbar('Erreur lors de la mise à jour', 'error');
    }
  };

  const handleToggleFeatured = async (id) => {
    try {
      await affiliateService.toggleReviewFeatured(id);
      showSnackbar('Mise en avant mise à jour');
      loadReviews(currentTab);
    } catch (error) {
      showSnackbar('Erreur lors de la mise à jour', 'error');
    }
  };

  const handleViewDetails = (review) => {
    setSelectedReview(review);
    setOpenDialog(true);
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Avis des affiliés
      </Typography>

      {/* Barre de recherche */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Rechercher par affilié, produit, commentaire..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            endAdornment: searchTerm && (
              <IconButton size="small" onClick={handleClearSearch}>
                <ClearIcon />
              </IconButton>
            ),
          }}
        />
      </Box>

      <Tabs value={currentTab} onChange={(e, value) => setCurrentTab(value)} sx={{ mb: 3 }}>
        <Tab label="Tous" value="all" />
        <Tab label="Publiés" value="published" />
        <Tab label="Non publiés" value="unpublished" />
      </Tabs>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Affilié</TableCell>
              <TableCell>Produit</TableCell>
              <TableCell>Note</TableCell>
              <TableCell>Commentaire</TableCell>
              <TableCell>Date</TableCell>
              <TableCell align="center">Publié</TableCell>
              <TableCell align="center">Mis en avant</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredReviews.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="text.secondary" sx={{ py: 3 }}>
                    {searchTerm ? 'Aucun résultat trouvé' : 'Aucun avis'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredReviews.map((review) => (
              <TableRow key={review.id}>
                <TableCell>
                  <Box>
                    <Typography variant="body2">
                      {review.affiliate?.user?.first_name} {review.affiliate?.user?.last_name}
                    </Typography>
                    <Chip 
                      label={review.affiliate?.affiliateCode} 
                      size="small" 
                      variant="outlined"
                    />
                  </Box>
                </TableCell>
                <TableCell>{review.product?.name}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Rating value={review.rating} readOnly size="small" />
                    <Typography variant="body2" sx={{ ml: 1 }}>
                      ({review.rating}/5)
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                    {review.comment || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  {new Date(review.createdAt).toLocaleDateString('fr-FR')}
                </TableCell>
                <TableCell align="center">
                  <Switch
                    checked={review.isPublished}
                    onChange={() => handleTogglePublish(review.id)}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <IconButton 
                    onClick={() => handleToggleFeatured(review.id)}
                    color={review.isFeatured ? 'warning' : 'default'}
                    size="small"
                  >
                    {review.isFeatured ? <StarIcon /> : <StarBorderIcon />}
                  </IconButton>
                </TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => handleViewDetails(review)}>
                    <ViewIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog détails */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Détails de l'avis</DialogTitle>
        <DialogContent>
          {selectedReview && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Affilié</Typography>
                <Typography>
                  {selectedReview.affiliate?.user?.first_name} {selectedReview.affiliate?.user?.last_name}
                  {' '}({selectedReview.affiliate?.affiliateCode})
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Produit</Typography>
                <Typography>{selectedReview.product?.name}</Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Note</Typography>
                <Rating value={selectedReview.rating} readOnly />
              </Grid>

              {selectedReview.comment && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Commentaire</Typography>
                  <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Typography>{selectedReview.comment}</Typography>
                  </Paper>
                </Grid>
              )}

              {selectedReview.images && selectedReview.images.length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="text.secondary">Images</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                    {selectedReview.images.map((img, idx) => (
                      <Box
                        key={idx}
                        component="img"
                        src={img}
                        sx={{
                          width: 100,
                          height: 100,
                          objectFit: 'cover',
                          borderRadius: 1,
                        }}
                      />
                    ))}
                  </Box>
                </Grid>
              )}

              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={selectedReview.isPublished}
                      onChange={() => {
                        handleTogglePublish(selectedReview.id);
                        setSelectedReview({ ...selectedReview, isPublished: !selectedReview.isPublished });
                      }}
                    />
                  }
                  label="Publié"
                />
              </Grid>

              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={selectedReview.isFeatured}
                      onChange={() => {
                        handleToggleFeatured(selectedReview.id);
                        setSelectedReview({ ...selectedReview, isFeatured: !selectedReview.isFeatured });
                      }}
                    />
                  }
                  label="Mis en avant"
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default AffiliateReviews;


